import React from 'react'
import { useSuggestChatResponseLazyQuery } from '~graphql/generated/graphql'
import { Icon } from '@chakra-ui/icons'
import { Button } from '@chakra-ui/react'
import { Spinner } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Global } from '@emotion/react'
import { FaMagic } from 'react-icons/fa'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import { MaxSuggestionsModal } from './max-suggestions-modal'

const rotate = keyframes`
    to {
        --angle: 360deg;
    }
`

interface SuggestChatResponseButtonProps {
    chatId: string
    text: string
    setText: (text: string) => void
    /** Force icon-only display regardless of breakpoint. */
    compact?: boolean
}

export default function SuggestChatResponseButton({
    chatId,
    text,
    setText,
    compact = false,
}: SuggestChatResponseButtonProps) {
    const { onError } = useToastFeedback(
        '',
        'An error occurred while suggesting a chat response.',
    )

    const { isOpen, onOpen, onClose } = useDisclosure()

    const [suggestChatResponse, { data, loading }] =
        useSuggestChatResponseLazyQuery()

    const handleSuggestChatResponse = async () => {
        const { data, error } = await suggestChatResponse({
            variables: { chatId: chatId, existingText: text },
        })
        if (error || !data?.suggestChatResponse) {
            console.error(error)
            onError()
            return
        }
        if (data?.suggestChatResponse.responses_left > 0) {
            setText(data.suggestChatResponse.response)
        } else {
            onOpen()
        }
    }

    return (
        <>
            {loading && (
                <Global
                    styles={`
                        @property --angle {
                            syntax: '<angle>';
                            initial-value: 0deg;
                            inherits: false;
                        }
                    `}
                />
            )}
            <Button
                leftIcon={
                    loading ? <Spinner size="xs" /> : <Icon as={FaMagic} />
                }
                size="xs"
                borderWidth={1}
                borderColor="gray.200"
                h="28px"
                rounded="full"
                variant="subtle"
                onClick={handleSuggestChatResponse}
                isDisabled={loading}
                display={
                    compact
                        ? 'none'
                        : {
                              base: 'none',
                              lg: 'flex',
                          }
                }
                sx={
                    loading
                        ? {
                              '--angle': '0deg',
                              borderColor: 'transparent',
                              position: 'relative',
                              '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: '-2px',
                                  background:
                                      'conic-gradient(from var(--angle), red, yellow, lime, aqua, blue, magenta, red)',
                                  borderRadius: 'full',
                                  animation: `${rotate} 2s linear infinite`,
                                  zIndex: -1,
                              },
                              '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: '0px',
                                  background:
                                      'var(--chakra-colors-chakra-body-bg)',
                                  borderRadius: 'full',
                                  zIndex: -1,
                              },
                          }
                        : undefined
                }
            >
                {text ? 'Improve' : 'Suggest'} Chat Response
            </Button>
            <IconButtonWithTooltip
                icon={
                    loading ? (
                        <Spinner size="xs" />
                    ) : (
                        <Icon as={FaMagic} fontSize="xs" />
                    )
                }
                label={`${text ? 'Improve' : 'Suggest'} Chat Response`}
                size="xs"
                w="28px"
                h="28px"
                rounded="full"
                variant="subtle"
                borderWidth={2}
                borderColor="gray.200"
                tooltipOverrides={{
                    placement: 'top',
                }}
                display={
                    compact
                        ? 'flex'
                        : {
                              base: 'flex',
                              lg: 'none',
                          }
                }
                onClick={handleSuggestChatResponse}
                isDisabled={loading}
                sx={
                    loading
                        ? {
                              '--angle': '0deg',
                              borderColor: 'transparent',
                              position: 'relative',
                              '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: '-2px',
                                  background:
                                      'conic-gradient(from var(--angle), red, yellow, lime, aqua, blue, magenta, red)',
                                  borderRadius: 'full',
                                  animation: `${rotate} 2s linear infinite`,
                                  zIndex: -1,
                              },
                              '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: '0px',
                                  background:
                                      'var(--chakra-colors-chakra-body-bg)',
                                  borderRadius: 'full',
                                  zIndex: -1,
                              },
                          }
                        : undefined
                }
            />
            <MaxSuggestionsModal isOpen={isOpen} onClose={onClose} />
        </>
    )
}
